import { GoogleAuthProvider, User } from "firebase/auth";
import { Navigate, useSearchParams } from "react-router-dom";
import { AuthContext, AuthContextType } from "../../context/AuthProvider";
import { Link } from "react-router-dom";
import React, {
	FC,
	ReactNode,
	useCallback,
	useContext,
	useEffect,
	useState,
	useRef
} from "react";
import { useMutation } from "@tanstack/react-query";
import ApiInterface from "../../services/api-client/api-interface";
import TermsAndPrivacy, {
	TermsAndPrivacyRef
} from "../../components/DesignSystem/Legal/TermsAndPrivacy";
import {
	API_HTTP,
	resumeAnalyzerApiEndpoints
} from "../../services/api-client/endpoints";
import { AxiosError } from "axios";
import { useMediaQuery } from "react-responsive";
import { Footer } from "../../components/DesignSystem/Contours/Footer";
import Spinner from "../../components/DesignSystem/Output/Spinner";
import {
	ProviderGoogle,
	ProviderLinkedin,
	ProviderMeta
} from "../../components/DesignSystem/Input/ProviderButton";
import {
	ActivateSubscriptionPayload,
	LinkedinLoginPayload,
	LinkedinLoginResponse,
	ValidateReferralResponse
} from "../../DataModels/DataModels";
import { BrandName } from "../../components/DesignSystem/Output/BrandName";
import { useSnackbar } from "../../services/api-client/useSnackbar";
import { useLinkedIn } from "react-linkedin-login-oauth2";

function LoginPage() {
	/* User Journey — FTUE Mode (session_id present)
	 * The user completed a Stripe checkout (free trial or paid subscription) and was
	 * redirected here with a session_id query parameter. After authenticating, the client
	 * calls POST /orders to activate the checkout session. For paid subscriptions this
	 * binds the subscription to the account; for free trial checkouts the session has no
	 * subscription and the call completes immediately. If a referral code is present in
	 * sessionStorage, POST /referrals/:code/redeem follows sequentially. The user is then
	 * redirected to /dashboard.
	 */
	/* User Journey — Non-FTUE Mode (no session_id)
	 * The user navigated directly to /login or was redirected here by an auth guard.
	 * If not logged in: SSO buttons are shown; on login, redirects to /dashboard.
	 * If already logged in: redirects to /dashboard immediately.
	 */

	/* SHOW additional login buttons */
	const showLinkedIn = true;
	const showMeta = false;

	/* Middle layer Funtionality */
	const [searchParams] = useSearchParams();
	const sessionId = searchParams.get("session_id");
	const isFtue = sessionId !== null;

	/* State  */
	const [agreements, setAgreements] = useState<boolean>(false);
	const termsRef = useRef<TermsAndPrivacyRef>(null);
	const {
		login,
		customLogin,
		logout,
		loading: authLoading,
		user
	} = useContext(AuthContext) as AuthContextType;
	const isLoggedIn = !authLoading && user !== null;
	const { showSnack } = useSnackbar();

	// Referral state from sessionStorage
	const [referralCode] = useState<string | null>(() => {
		try {
			return sessionStorage.getItem("referral_code");
		} catch {
			return null;
		}
	});
	const [referralInfo] = useState<ValidateReferralResponse | null>(() => {
		try {
			const stored = sessionStorage.getItem("referral_info");
			return stored ? JSON.parse(stored) : null;
		} catch {
			return null;
		}
	});

	const isReferral = isFtue && referralCode !== null;

	const isTabletOrDesktop = useMediaQuery({ query: "(min-width: 500px)" });

	/* Comms - Referral Redeem */
	const referralRedeemMutation = useMutation({
		mutationFn: async ({
			authenticatedUser,
			code
		}: {
			authenticatedUser: User;
			code: string;
		}): Promise<void> => {
			const apiClient = new ApiInterface(
				API_HTTP.resumeAnalyzer,
				authenticatedUser
			);
			await apiClient.post(
				resumeAnalyzerApiEndpoints.referralRedeem,
				{},
				{ code }
			);
		},
		onSettled: (_, error) => {
			if (error) {
				console.error("Referral redeem error:", error);
			}
			try {
				sessionStorage.removeItem("referral_code");
				sessionStorage.removeItem("referral_info");
			} catch {
				/* no sessionStorage in test env */
			}
		}
	});

	const referralSettled =
		!isReferral ||
		referralRedeemMutation.isSuccess ||
		referralRedeemMutation.isError;

	/* Comms - API */
	const firstTimeSignInMutation = useMutation({
		mutationFn: async ({
			authenticatedUser,
			sessionId
		}: {
			authenticatedUser: User;
			sessionId: string;
		}): Promise<void> => {
			const apiClient = new ApiInterface(
				API_HTTP.resumeAnalyzer,
				authenticatedUser
			);
			const payload: ActivateSubscriptionPayload = {
				action: "activate-subscription",
				checkoutSessionId: sessionId
			};
			const response = await apiClient.post(
				resumeAnalyzerApiEndpoints.orders,
				payload,
				{},
				{ timeout: 120 * 1000 }
			);
			return response;
		},
		onError: async (error) => {
			// Log the user out so they can try again
			await logout();
			// Handle specific known errors
			if (
				error instanceof AxiosError &&
				error.name === "AxiosError" &&
				error.code === "ERR_BAD_REQUEST" &&
				(error.status === 403 || error.status === 409)
			) {
				/* 403 FORBIDDEN status code with "client_reference_id_mismatch"
				detail indicates that the user is attempting to activate a
				subscription that was purchased by another account (edge case) */
				const errorDetail = error.response?.data?.error;
				if (error.status === 403) {
					if (errorDetail === "client_reference_id_mismatch") {
						showSnack("NotifyAccountMismatch", "dark");
					} else {
						// If the detail isn't present, this is most likely a regular 403 error
						showSnack("NotifyLoginError", "dark");
					}
				} else if (error.status === 409) {
					/* 409 CONFLICT status code indicates that the user is attempting to
				activate another subscription even though they already have one (edge case) */
					showSnack("NotifyAccountConflict", "dark");
				}
			}
			// Handle unknown errors
			else {
				console.error("Login error:", error);
				showSnack("NotifyLoginError", "dark");
			}
		}
	});

	const ftueComplete = !isFtue || firstTimeSignInMutation.isSuccess;

	useEffect(() => {
		// Trigger mutation on mount when user is logged in, session_id is present, and mutation has not run yet
		if (isLoggedIn && isFtue && firstTimeSignInMutation.isIdle) {
			firstTimeSignInMutation.mutate({ authenticatedUser: user, sessionId });
		}
		// Redeem referral code after FTUE completes
		if (
			isLoggedIn &&
			isReferral &&
			referralRedeemMutation.isIdle &&
			ftueComplete
		) {
			referralRedeemMutation.mutate({
				authenticatedUser: user,
				code: referralCode!
			});
		}
	});

	const linkedinLoginMutation = useMutation({
		mutationFn: async ({ code }: { code: string }) => {
			const apiClient = new ApiInterface(API_HTTP.resumeAnalyzer);
			const payload: LinkedinLoginPayload = { code };
			const response: LinkedinLoginResponse = await apiClient.post(
				resumeAnalyzerApiEndpoints.linkedinLogin,
				payload
			);
			return response.token;
		},
		onSuccess: async (token) => {
			try {
				const { user: authenticatedUser } = await customLogin(token);
				if (isFtue) {
					firstTimeSignInMutation.mutate({ authenticatedUser, sessionId });
				}
			} catch (error: any) {
				console.error("Login error:", error);
				showSnack("NotifyLoginError", "dark");
			}
		}
	});

	const isWaitingForAuth = authLoading || linkedinLoginMutation.isPending;
	const showSpinner = isLoggedIn || isWaitingForAuth;

	const linkedInCodeProcessed = useRef(false);
	const handleLinkedInSuccess = useCallback(async (linkedInCode: string) => {
		if (linkedInCodeProcessed.current) return;
		linkedInCodeProcessed.current = true;
		linkedinLoginMutation.mutate({ code: linkedInCode });
		// This rule is disabled here because linkedinLoginMutation is a stable reference
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const { linkedInLogin } = useLinkedIn({
		clientId: process.env.REACT_APP_LINKEDIN_CLIENT_ID ?? "",
		redirectUri: `${window.location.origin}/linkedin-login`,
		onSuccess: handleLinkedInSuccess,
		onError: (error: any) => {
			const ignoredErrors = ["user_closed_popup", "user_cancelled_login"];
			if (!ignoredErrors.includes(error.error)) {
				console.log(error);
			}
		},
		scope: "email openid profile"
	});

	const linkedinLoginHandler = async () => {
		/* Validate User Agrees */
		if (!agreements) {
			termsRef.current?.triggerValidation();
			return;
		}
		linkedInCodeProcessed.current = false; // reset for each new attempt
		linkedInLogin();
	};

	const loginHandler = async (): Promise<void> => {
		/* Validate User Agrees */
		if (!agreements) {
			termsRef.current?.triggerValidation();
			return;
		}

		/* Call Provider*/
		const googleProvider = new GoogleAuthProvider();
		googleProvider.setCustomParameters({
			prompt: "select_account"
		});

		// Start login process
		await login(googleProvider)
			.then(({ user: authenticatedUser }) => {
				if (isFtue) {
					firstTimeSignInMutation.mutate({ authenticatedUser, sessionId });
				}
			})
			.catch((error: any) => {
				// Handle login errors
				// (Closing the popup is not a "real" error)
				if (error.code !== "auth/popup-closed-by-user") {
					console.error("Login error:", error);
					showSnack("NotifyLoginError", "dark");
				}
			});
	};

	const uxLoadingSpinner = (): ReactNode => {
		return (
			<div className="mt-2">
				<p className="text-center text-sm text-primary pl-2 pr-2">
					{/* See: https://github.com/firebase/firebase-js-sdk/issues/8061#issuecomment-2047370693 */}
					Please wait up to 10 seconds if the popup was closed without logging
					in
				</p>
				<div className="relative h-20 my-2">
					<Spinner className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2" />
				</div>
			</div>
		);
	};

	const uxLoginWidget = (): ReactNode => {
		return (
			<div className="mt-2 pb-6">
				{/* Login buttons */}
				<div className="mb-4 pl-2 pr-2">
					<ProviderGoogle isFute={isFtue} onClick={loginHandler} />
				</div>
				{showLinkedIn && (
					<div className="mb-4 pl-2 pr-2">
						<ProviderLinkedin isFute={isFtue} onClick={linkedinLoginHandler} />
					</div>
				)}
				{showMeta && (
					<div className="mb-4 pl-2 pr-2">
						<ProviderMeta isFute={isFtue} onClick={loginHandler} />
					</div>
				)}

				{/* Terms Checkbox*/}
				<TermsAndPrivacy
					ref={termsRef}
					checked={agreements}
					onCheckboxChange={setAgreements}
					termsUrl="/terms"
					privacyUrl="/privacy"
				/>
			</div>
		);
	};

	return (
		<>
			{isLoggedIn && ftueComplete && referralSettled ? (
				// Redirect to the dashboard when user is logged in and mutations are complete
				// The PrivateRouteWrapper will handle access control
				<Navigate to="/dashboard" />
			) : (
				<div className="content">
					<div className="min-h-screen bg-primary-background flex flex-col">
						<div className="m-auto"></div>
						{/* TODO: Use Tailwind sm: feature to simplify this CSS. */}
						<div
							className={
								"text-primary flex flex-col items-center justify-center" +
								(isTabletOrDesktop ? " mt-8" : "")
							}
						>
							{/* Content container */}
							<div
								className={
									"h-full flex flex-col overflow-clip " +
									(isTabletOrDesktop
										? "rounded-3xl border border-primary-inactive max-w-[402px]"
										: "")
								}
							>
								<UxTitle />
								{isReferral && referralInfo && referralInfo.valid && (
									<div className="bg-green-900/30 border border-green-700 rounded-xl p-3 mx-4 mt-2 text-center">
										<p className="text-green-300 text-sm font-semibold">
											{referralInfo.referralType === "friend"
												? `${referralInfo.originatorName} invited you!`
												: referralInfo.originatorName}
										</p>
										<p className="text-green-400 text-xs mt-1">
											Sign in to claim your referral bonus.
										</p>
									</div>
								)}
								{!isReferral && <UxMascot />}
								{!isReferral && <UxMappyText />}
								<UxNewLandingWidget />
								<UxOrWidget />

								{/*Main Content */}
								{showSpinner ? uxLoadingSpinner() : uxLoginWidget()}

								{!isTabletOrDesktop && <UxFooter />}
							</div>
						</div>
						<div className="m-auto"></div>
						<div className="items-bottom justify-center">
							{isTabletOrDesktop && <Footer />}
						</div>
					</div>
				</div>
			)}
		</>
	);
}

const UxTitle: FC = () => {
	return (
		// Dark banner with logo section
		<div
			className="relative pt-8 pb-16 bg-cover bg-center"
			style={{ backgroundImage: "url(./headerContour.png)" }}
		>
			<BrandName theme="dark" className="ml-4 mb-2" size="large" />
		</div>
	);
};

const UxMascot: FC = () => {
	return (
		// mascot circle
		// This overlaps (-mt-12) the header
		<div className="relative -mt-12 px-6">
			<div className="w-24 h-24 mx-auto">
				<img
					src="../mappy-icon-BW.png"
					alt="Mappy mascot"
					className="w-full object-cover"
				/>
			</div>
		</div>
	);
};

const UxMappyText: FC = () => {
	return (
		// Welcome text
		<div className="text-center mt-6 mb-2 px-8">
			<h2 className="text-2xl font-normal font-xl text-primary">
				I’m Mappy and I’m looking forward to helping you.
			</h2>
		</div>
	);
};

const UxNewLandingWidget: FC = () => {
	return (
		<div className="text-center font-base mt-2 text-primary">
			<Link className="font-bold underline pr-1" to="/landing">
				Explore JobMap
			</Link>
		</div>
	);
};

const UxOrWidget: FC = () => {
	return (
		<div className="text-center mt-6 mb-8 text-primary">
			<div className="text-2xl font-normal font-xl ">or</div>
		</div>
	);
};

const UxFooter: FC = () => {
	return (
		<div
			className="bg-cover bg-center"
			style={{ backgroundImage: "url(./footerContour.png)" }}
		>
			<div className=" mt-[187px]">&nbsp;</div>
		</div>
	);
};

export default LoginPage;
